/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH
 * under one or more contributor license agreements. Licensed under a proprietary license.
 * See the License.txt file for more information. You may not use this file
 * except in compliance with the proprietary license.
 */

import {useEffect, useState} from 'react';
import {Link, matchPath, useLocation} from 'react-router-dom';
import {C3Navigation, C3NavigationProps} from '@camunda/camunda-composite-components';

// @ts-ignore
import {NavItem} from 'components';
import {showError} from 'notifications';
import {t} from 'translation';
import {useDocs, useErrorHandling} from 'hooks';

import {isEventBasedProcessEnabled} from './service';
import useUserMenu from './useUserMenu';

import './Header.scss';

export default function Header({noActions}: {noActions?: boolean}) {
  const [showEventBased, setShowEventBased] = useState(false);
  const location = useLocation();
  const {mightFail} = useErrorHandling();
  const {getBaseDocsUrl} = useDocs();
  const userSideBar = useUserMenu();
  useEffect(() => {
    mightFail(
      isEventBasedProcessEnabled(),
      (enabled) => {
        setShowEventBased(enabled);
      },
      showError
    );
  }, [mightFail]);

  const props: C3NavigationProps = {
    app: createAppProps(location),
    appBar: {},
    navbar: {elements: []},
  };

  if (!noActions) {
    props.navbar = createNavBarProps(showEventBased, location.pathname);
    props.infoSideBar = createInfoSideBarProps(getBaseDocsUrl());
    props.userSideBar = userSideBar;
  }

  return <C3Navigation {...props} />;
}

function createAppProps(location: {pathname: string}): C3NavigationProps['app'] {
  return {
    name: t('appName').toString(),
    ariaLabel: t('appFullName').toString(),
    routeProps: {
      as: Link,
      className: 'cds--header__name',
      to: '/',
      replace: location.pathname === '/',
    },
  };
}

function createNavBarProps(showEventBased: boolean, pathname: string): C3NavigationProps['navbar'] {
  const elements: C3NavigationProps['navbar']['elements'] = [
    {
      key: 'dashboards',
      label: t('navigation.dashboards').toString(),
      routeProps: {
        as: NavItem,
        name: t('navigation.dashboards'),
        linksTo: '/',
        active: ['/', '/processes/', '/processes/*'],
        breadcrumbsEntities: [{entity: 'report'}],
      },
      isCurrentPage: isCurrentPage(['/', '/processes/', '/processes/*'], pathname),
    },
    {
      key: 'collections',
      label: t('navigation.collections').toString(),
      routeProps: {
        as: NavItem,
        name: t('navigation.collections'),
        linksTo: '/collections',
        active: ['/collections/', '/report/*', '/dashboard/*', '/collection/*'],
        breadcrumbsEntities: [{entity: 'collection'}, {entity: 'dashboard'}, {entity: 'report'}],
      },
      isCurrentPage: isCurrentPage(
        ['/collections/', '/report/*', '/dashboard/*', '/collection/*'],
        pathname
      ),
    },
    {
      key: 'analysis',
      label: t('navigation.analysis').toString(),
      routeProps: {
        as: NavItem,
        name: t('navigation.analysis'),
        linksTo: '/analysis',
        active: ['/analysis/', '/analysis/*'],
      },
      isCurrentPage: isCurrentPage(['/analysis/', '/analysis/*'], pathname),
    },
  ];

  if (showEventBased) {
    elements.push({
      key: 'events',
      label: t('navigation.events').toString(),
      routeProps: {
        as: NavItem,
        name: t('navigation.events'),
        linksTo: '/events/processes/',
        active: ['/events/processes/', '/events/ingested/', '/events/processes/*'],
        breadcrumbsEntities: [{entity: 'eventBasedProcess', entityUrl: 'events/processes'}],
      },
      isCurrentPage: isCurrentPage(
        ['/events/processes/', '/events/ingested/', '/events/processes/*'],
        pathname
      ),
    });
  }

  return {
    elements,
    tags: [],
  };
}

function isCurrentPage(active: string[], pathname: string): boolean {
  return matchPath(pathname, {path: active, exact: true}) !== null;
}

function createInfoSideBarProps(docsUrl: string): C3NavigationProps['infoSideBar'] {
  return {
    tooltip: t('navigation.info.open').toString(),
    elements: [
      {
        key: 'documentation',
        label: t('navigation.documentation').toString(),
        onClick: () => {
          window.open(docsUrl, '_blank');
        },
      },
      {
        key: 'academy',
        label: t('navigation.academy').toString(),
        onClick: () => {
          window.open('https://academy.camunda.com/', '_blank');
        },
      },
      {
        key: 'feedbackAndSupport',
        label: t('navigation.feedback').toString(),
        onClick: () => {
          window.open('https://jira.camunda.com/projects/SUPPORT/queues', '_blank');
        },
      },
      {
        key: 'slackCommunityChannel',
        label: t('navigation.slack').toString(),
        onClick: () => {
          window.open('https://camunda.com/slack', '_blank');
        },
      },
    ],
  };
}
